import React, { useState, useMemo } from 'react';
import { ChartBarIcon, UsersIcon, CurrencyDollarIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { ChartBarIcon as ChartBarIconSolid } from '@heroicons/react/24/solid';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

interface OverallStatusSectionProps {
  totalMembers: number;
  activeNewcomers: number;
  weeklyIncome: number;
  weeklyExpense: number;
  netIncome: number;
  profitMargin: string;
  attendanceRate: number;
  activeEvents: number;
}

// 색상 상수
const STATUS_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'];
const FINANCE_COLORS = ['#059669', '#DC2626', '#1D4ED8'];
const ACTIVITY_COLORS = ['#7C3AED', '#EC4899', '#06B6D4', '#F97316'];

export const OverallStatusSection: React.FC<OverallStatusSectionProps> = ({
  totalMembers,
  activeNewcomers,
  weeklyIncome,
  weeklyExpense,
  netIncome,
  profitMargin,
  attendanceRate,
  activeEvents
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'finance' | 'activity' | 'trends'>('overview');

  // 전체 현황 데이터 생성
  const overallData = useMemo(() => [
    {
      name: '등록 교인',
      value: totalMembers,
      color: '#10B981',
      percentage: 100,
      description: '총 등록된 교인 수'
    },
    {
      name: '활동 새가족',
      value: activeNewcomers,
      color: '#3B82F6',
      percentage: totalMembers > 0 ? ((activeNewcomers / totalMembers) * 100).toFixed(1) : '0',
      description: '현재 활동 중인 새가족'
    },
    {
      name: '주간 출석률',
      value: attendanceRate,
      color: '#F59E0B',
      percentage: attendanceRate,
      description: '주간 예배 출석률'
    },
    {
      name: '진행 행사',
      value: activeEvents,
      color: '#EF4444',
      percentage: (activeEvents / 20) * 100, // 최대 20개 행사 가정
      description: '현재 진행 중인 행사'
    }
  ], [totalMembers, activeNewcomers, attendanceRate, activeEvents]);

  // 재정 데이터
  const financeData = useMemo(() => [
    {
      name: '주간 수입',
      value: weeklyIncome,
      color: '#059669',
      type: 'income'
    },
    {
      name: '주간 지출',
      value: weeklyExpense,
      color: '#DC2626',
      type: 'expense'
    },
    {
      name: '순 손익',
      value: netIncome,
      color: netIncome >= 0 ? '#1D4ED8' : '#DC2626',
      type: 'net'
    }
  ], [weeklyIncome, weeklyExpense, netIncome]);

  // 활동 트렌드 데이터 (가상 데이터 - 실제로는 props로 받아야 함)
  const activityTrendData = useMemo(() => [
    { month: '1월', 교인수: totalMembers - 15, 새가족: activeNewcomers - 3, 출석률: attendanceRate - 5, 행사수: activeEvents - 2 },
    { month: '2월', 교인수: totalMembers - 12, 새가족: activeNewcomers - 2, 출석률: attendanceRate - 3, 행사수: activeEvents - 1 },
    { month: '3월', 교인수: totalMembers - 8, 새가족: activeNewcomers - 1, 출석률: attendanceRate - 1, 행사수: activeEvents },
    { month: '4월', 교인수: totalMembers - 5, 새가족: activeNewcomers, 출석률: attendanceRate + 2, 행사수: activeEvents + 1 },
    { month: '5월', 교인수: totalMembers - 2, 새가족: activeNewcomers + 1, 출석률: attendanceRate + 1, 행사수: activeEvents },
    { month: '6월', 교인수: totalMembers, 새가족: activeNewcomers, 출석률: attendanceRate, 행사수: activeEvents }
  ], [totalMembers, activeNewcomers, attendanceRate, activeEvents]);

  // 건강도 점수 계산
  const healthScore = useMemo(() => {
    const memberScore = Math.min((totalMembers / 100) * 25, 25); // 최대 25점
    const newcomerScore = Math.min((activeNewcomers / 10) * 20, 20); // 최대 20점
    const attendanceScore = (attendanceRate / 100) * 30; // 최대 30점
    const financeScore = netIncome > 0 ? 25 : (netIncome / Math.abs(netIncome || 1)) * 10 + 15; // 최대 25점
    
    return Math.round(memberScore + newcomerScore + attendanceScore + financeScore);
  }, [totalMembers, activeNewcomers, attendanceRate, netIncome]);

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl">
            <ChartBarIconSolid className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">전체 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">교회 전반적인 상황과 핵심 지표 분석</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            healthScore >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            healthScore >= 60 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-2 inline-block ${
              healthScore >= 80 ? 'bg-green-500' :
              healthScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            }`}></span>
            건강도 {healthScore}점
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 개요
        </button>
        <button
          onClick={() => setActiveTab('finance')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'finance'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          재정 현황
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'activity'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          활동 현황
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'trends'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          종합 트렌드
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 핵심 지표 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 등록 교인</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalMembers.toLocaleString()}명</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <UsersIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">주간 순손익</p>
                <p className={`text-2xl font-bold ${netIncome >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {netIncome >= 0 ? '+' : ''}{netIncome.toLocaleString()}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${netIncome >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                <CurrencyDollarIcon className={`h-5 w-5 ${netIncome >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">주간 출석률</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{attendanceRate}%</p>
              </div>
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-3">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">종합 현황 개요</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">핵심 지표 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie 
                        data={overallData} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={70} 
                        labelLine={false} 
                        label={{ fontSize: 12 }}
                      >
                        {overallData.map((entry, index) => (
                          <Cell key={`cell-overall-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value}`, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">상세 현황</h4>
                  <div className="space-y-3">
                    {overallData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <span className="font-medium text-gray-800 dark:text-white">
                          {item.name === '주간 출석률' ? `${item.value}%` : `${item.value.toLocaleString()}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'finance' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">재정 현황 상세</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">수입/지출 비교</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={financeData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${Number(value).toLocaleString()} HUF`, '금액']} />
                      <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                        {financeData.map((entry, index) => (
                          <Cell key={`cell-finance-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">재정 요약</h4>
                  <div className="space-y-4">
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <p className="text-sm text-green-600 dark:text-green-400 mb-1">주간 수입</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">
                        {weeklyIncome.toLocaleString()} HUF
                      </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400 mb-1">주간 지출</p>
                      <p className="text-xl font-bold text-red-800 dark:text-red-200">
                        {weeklyExpense.toLocaleString()} HUF
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${netIncome >= 0 ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                      <p className={`text-sm mb-1 ${netIncome >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                        순손익 ({profitMargin}%)
                      </p>
                      <p className={`text-xl font-bold ${netIncome >= 0 ? 'text-blue-800 dark:text-blue-200' : 'text-red-800 dark:text-red-200'}`}>
                        {netIncome >= 0 ? '+' : ''}{netIncome.toLocaleString()} HUF
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">활동 현황 분석</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">활동 새가족</p>
                  <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">{activeNewcomers}명</p>
                </div>
                <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
                  <p className="text-sm text-pink-600 dark:text-pink-400 mb-1">진행 행사</p>
                  <p className="text-2xl font-bold text-pink-800 dark:text-pink-200">{activeEvents}개</p>
                </div>
                <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
                  <p className="text-sm text-cyan-600 dark:text-cyan-400 mb-1">주간 출석률</p>
                  <p className="text-2xl font-bold text-cyan-800 dark:text-cyan-200">{attendanceRate}%</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                  <p className="text-sm text-orange-600 dark:text-orange-400 mb-1">건강도 점수</p>
                  <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">{healthScore}점</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">활동 지표 요약</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 새가족 정착률: {totalMembers > 0 ? ((activeNewcomers / totalMembers) * 100).toFixed(1) : 0}%</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 행사 참여도: 활발</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 전반적 활동성: {healthScore >= 70 ? '우수' : healthScore >= 50 ? '양호' : '개선 필요'}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 출석 안정성: {attendanceRate >= 80 ? '안정적' : '개선 필요'}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 성장 추세: {activeNewcomers > 0 ? '성장' : '정체'}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">• 종합 평가: {healthScore >= 80 ? 'A급' : healthScore >= 60 ? 'B급' : 'C급'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">종합 트렌드 분석</h3>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">6개월 추세</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={activityTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="교인수" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="새가족" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="출석률" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="행사수" stroke="#EF4444" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">성장 추세</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">상승</p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">안정성 지수</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-200">{attendanceRate >= 80 ? '높음' : '보통'}</p>
                    </div>
                    <UsersIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">종합 전망</p>
                      <p className="text-xl font-bold text-purple-800 dark:text-purple-200">긍정적</p>
                    </div>
                    <CurrencyDollarIcon className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};